/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext, useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { ApiContext } from '../../../../context/ApiContext';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../shared/Utils/Constants';
import {
  IEmaasEnergyUsageComponentDataArea,
  IAny
} from '../../IEnergyManagementProps';
import {
  convertDataForBreakdownByMeter,
  convertDataForBreakdownByMeterWithIndividualScaling,
  convertUnitForPerformanceGraph,
  getPerformanceGraphData,
  getPerformanceGraphDataNew,
  getSpanDetails
} from '../../../shared/Utils/Utils';
import { getProgressBarUnitScaledData } from '../../../shared/Utils/portfolioDataFormatter';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import {
  getNullCheckedAggregationEnergyCardData,
  getNullToZero
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { getEnergyEndDate } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import CommonEnergyComponent from '../../../shared/EnergyKPI/CommonEnergyComponent';
import { EnergyUsageField } from '../../../shared/Enums';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import { getConvertedValue } from '../../../shared/Utils/unitHandling/unitHandling';
import _ from 'lodash';
import CommonEnergyComponentSeparate from '../../../shared/EnergyKPI/CommonEnergyComponentSeparate';

interface IEmaasEnergyUsageComponentProps {
  data: IEmaasEnergyUsageComponentDataArea;
  forGraph?: boolean;
  forTable?: boolean;
  forSummary?: boolean;
}

export const EmaasEnergyUsageComponent = (
  props: IEmaasEnergyUsageComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { data } = props;
  const apiContext = useContext(ApiContext);
  const {
    onEnergyUsageViewDetailsClick,
    isBreakdownEquipment,
    userPreferedUnit
  } = apiContext;
  const onViewDetailsClick = () => {
    if (onEnergyUsageViewDetailsClick) {
      onEnergyUsageViewDetailsClick(
        _.toLower(data.sourceType) === 'water' ? 'water' : ''
      );
    }
  };

  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;

  // const {
  //   summary: {
  //     totalConsumption,
  //     totalBaselineConsumption,
  //     totalEnergyDeviation,
  //     totalEnergyDeviationPercentage,
  //     absoluteTotalBaselineConsumption,
  //     absoluteTotalConsumption
  //   },
  //   performanceAggregation,
  //   aggregationsStartDate,
  //   aggregationsEndDate,
  //   performanceAggregationApplied,
  //   energyMeasurementUnit,
  //   areaWiseAggregations,
  //   breakdownByMeter,
  //   sourceType
  // } = data;

  const SpanDetails = getSpanDetails(
    data.aggregationsStartDate,
    data.aggregationsEndDate
  );

  let consumptionBreakdownMeterData: any;

  if (props.forTable) {
    consumptionBreakdownMeterData = convertDataForBreakdownByMeterWithIndividualScaling(
      isBreakdownEquipment ? data.breakdownByMeter : data.areaWiseAggregations,
      _.toLower(data.sourceType) === 'water'
        ? data.energyMeasurementUnit
        : fromUnit,
      [],
      data.energyMeasurementUnit,
      apiContext.showChangeWhenData,
      _.toLower(data.sourceType) === 'water'
        ? ConversionType.WATER
        : ConversionType.ELECTRICITY
    );
  }
  let convertedPerformanceData: any;

  if (props.forGraph) {
    convertedPerformanceData = convertUnitForPerformanceGraph(
      data.performanceAggregation,
      _.toLower(data.sourceType) === 'water'
        ? data.energyMeasurementUnit
        : fromUnit,
      data.energyMeasurementUnit,
      _.toLower(data.sourceType) === 'water'
        ? ConversionType.WATER
        : ConversionType.ELECTRICITY
    );
  }

  let values: any;
  if (props.forTable) {
    values = consumptionBreakdownMeterData.values;
  }

  let performanceDisplayUnit: any;

  if (props.forGraph) {
    performanceDisplayUnit = convertedPerformanceData.displayUnit;
  }

  const [usageType, setUsageType] = useState<string>(t(d.HIGHWITHOUTUNIT));

  let progressBarUnitScaledData: any;
  let comparisonData: any;

  if (props.forSummary) {
    progressBarUnitScaledData = getProgressBarUnitScaledData(
      _.toLower(data.sourceType) === 'water'
        ? ConversionType.WATER
        : ConversionType.ELECTRICITY,
      {
        previousPeriod: data.summary.totalBaselineConsumption,
        actual: data.summary.totalConsumption
      },
      data.summary.totalEnergyDeviation,
      _.toLower(data.sourceType) === 'water'
        ? data.energyMeasurementUnit
        : fromUnit, //energyMeasurementUnit,
      undefined,
      data.summary.absoluteTotalBaselineConsumption,
      data.summary.absoluteTotalConsumption,
      data.energyMeasurementUnit,
      apiContext.showChangeWhenData,
      true
    );
  }
  if (props.forGraph) {
    comparisonData =
      data.performanceAggregationApplied === 'Hourly'
        ? getPerformanceGraphData(
            data.performanceAggregation,
            data.energyMeasurementUnit,
            performanceDisplayUnit,
            data.performanceAggregationApplied,
            _.toLower(data.sourceType) === 'water'
              ? ConversionType.WATER
              : ConversionType.ELECTRICITY
          )
        : getPerformanceGraphDataNew(
            data.performanceAggregation,
            data.energyMeasurementUnit,
            performanceDisplayUnit,
            data.performanceAggregationApplied,
            _.toLower(data.sourceType) === 'water'
              ? ConversionType.WATER
              : ConversionType.ELECTRICITY,
            data.aggregationsStartDate,
            data.aggregationsEndDate
          );
  }

  let isPercentageGreater: any;
  if (props.forSummary) {
    isPercentageGreater =
      Math.abs(
        Number(
          getNullToZero(data.summary.totalEnergyDeviationPercentage)?.toFixed(
            KPI_PERCENTAGE_DECIMAL_PRECISION
          )
        )
      ) > PERCENT_CHANGE_THRESHOLD;
  }

  let aggregationEnergyData: any;

  if (props.forSummary) {
    aggregationEnergyData = {
      actual: progressBarUnitScaledData.actual,
      previousPeriod: progressBarUnitScaledData.previousPeriod,
      absoluteActual: progressBarUnitScaledData.absoluteActual,
      absolutePrevPeriod: progressBarUnitScaledData.absolutePreviousPeriod,
      actualUnit: progressBarUnitScaledData.actualUnit,
      baselineUnit: progressBarUnitScaledData.baselineUnit,
      changeUnit: progressBarUnitScaledData.changeUnit,
      savings:
        apiContext.showChangeWhenData &&
        progressBarUnitScaledData.deviation !== null &&
        isPercentageGreater
          ? null
          : progressBarUnitScaledData.deviation,
      savingsPercentage: parseFloat(
        getNullToZero(data.summary.totalEnergyDeviationPercentage)?.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      )
    };
  }
  const headers = [];
  if (
    data?.addOn?.includes('NILM') &&
    props.data.subscriptionType === EnergyUsageField.PREMIUMSUBS
  ) {
    headers.push(
      {
        HeaderValue: t(d.BREAKDOWNBYAREAS),
        TextAlignValue: 'left',
        Field: 'type'
      },
      {
        HeaderValue: usageType,
        TextAlignValue: 'right',
        Field: 'high',
        sortable: true
      },
      {
        HeaderValue: t(d.CHANGE),
        TextAlignValue: 'right',
        Field: 'change',
        sortable: true
      }
    );
  } else {
    if (isBreakdownEquipment) {
      headers.push(
        {
          HeaderValue: t(d.BREAKDOWNBYEQUIPMENTTYPE),
          TextAlignValue: 'left',
          Field: 'type'
        },
        {
          HeaderValue: t(d.HIGHWITHOUTUNIT),
          TextAlignValue: 'right',
          Field: 'high',
          sortable: true
        }
      );
    } else {
      headers.push(
        {
          HeaderValue: t(d.BREAKDOWNBYAREAS),
          TextAlignValue: 'left',
          Field: 'type'
        },
        {
          HeaderValue: usageType,
          TextAlignValue: 'right',
          Field: 'high',
          sortable: true
        },
        {
          HeaderValue: t(d.CHANGE),
          TextAlignValue: 'right',
          Field: 'change',
          sortable: true
        }
      );
    }
  }

  let isNullCheckedEnergyCardData: IAny = {};
  if (props.forSummary) {
    isNullCheckedEnergyCardData = getNullCheckedAggregationEnergyCardData(
      aggregationEnergyData,
      data.summary,
      apiContext.showChangeWhenData
    );
  }

  // const isNullCheckedEnergyCardData = getNullCheckedAggregationEnergyCardData(
  //   aggregationEnergyData,
  //   data.summary,
  //   apiContext.showChangeWhenData
  // );
  const onSortingHandler = (sortOrder: number): void => {
    sortOrder === 1
      ? setUsageType(t(d.LOWWITHNOUNIT))
      : setUsageType(t(d.HIGHWITHOUTUNIT));
  };
  const energyComponentProps = {
    onViewDetailsClick: onViewDetailsClick,
    ...(props.forSummary && {
      progressBarData: {
        aggregationData: isNullCheckedEnergyCardData,
        unit: progressBarUnitScaledData.ConvertedUnit,
        deviationScaledData: progressBarUnitScaledData.deviationScaledData
      }
    }),
    ...(props.forGraph && {
      graphData: {
        chartSeries: comparisonData,
        startTime: data.aggregationsStartDate,
        endTime: data.aggregationsEndDate,
        aggregationType: data.performanceAggregationApplied
      }
    }),
    ...(props.forTable && {
      tableData: {
        columnHeaders: headers as IDataTableHeader[],
        meterData: values
      }
    }),
    dateData: SpanDetails,
    onSortingHandler: onSortingHandler,
    energyMeasurementUnit: data.energyMeasurementUnit,
    fromCarbonKPI: true,
    originalUnit: data.energyMeasurementUnit,
    conversionType:
      _.toLower(data.sourceType) === 'water'
        ? ConversionType.WATER
        : ConversionType.ELECTRICITY,
    sourceType: data.sourceType
  };
  return (
    <CommonEnergyComponentSeparate
      forSummary={props.forSummary}
      forGraph={props.forGraph}
      forTable={props.forTable}
      {...energyComponentProps}
    />
  );
};
export default memo(EmaasEnergyUsageComponent);
